import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { LogIn, User, Lock, ArrowRight, Activity } from 'lucide-react';
import auth from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

export default function LoginPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = auth.login(formData.username, formData.password, formData.rememberMe);
      
      if (result.success) {
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Login failed');
      }
    } catch (error) {
      toast.error('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-white relative overflow-hidden">
      {/* Grid Pattern Background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.5) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(15, 23, 42, 0.5) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }} />

      {/* Background Image - Grayscale */}
      <div 
        className="absolute inset-0 opacity-10 grayscale"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758670496782-cb11b7b56675?q=80&w=2000&auto=format&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />

      {/* Abstract Elements */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-accent opacity-5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-48 h-48 bg-primary opacity-10 rounded-none" />
      
      {/* Main Card */}
      <div className="w-full max-w-md relative z-10">
        {/* System Status Badge */}
       

        {/* Main Card with Swiss Brutalist Style */}
        <div className="bg-white border-8 border-slate-900 shadow-[15px_15px_0px_0px_rgba(0,47,167,1)] relative">
          {/* Header */}
          <div className="p-8 border-b-4 border-slate-900 bg-slate-50">
            <div className="flex items-center justify-center mb-4">
              <div className="w-20 h-20 bg-slate-900 flex items-center justify-center">
                <LogIn className="text-white" size={40} />
              </div>
            </div>
            <h1 className="text-4xl font-black font-heading tracking-tighter text-center uppercase mb-2">
              Retail<span className="text-primary">Flow</span>
            </h1>
            <p className="text-center text-slate-600 font-medium uppercase text-sm tracking-wide">
              System Authentication Portal
            </p>
          </div>

          {/* Form Content */}
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username Field */}
              <div className="space-y-3">
                <Label htmlFor="username" className="text-sm font-black uppercase tracking-widest text-slate-900">
                  Username
                </Label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-12 bg-slate-900 flex items-center justify-center border-r-4 border-slate-900">
                    <User className="text-white" size={18} />
                  </div>
                  <Input
                    id="username"
                    data-testid="login-username"
                    type="text"
                    placeholder="ENTER USERNAME"
                    className="pl-16 h-14 border-4 border-slate-900 rounded-none font-mono text-sm uppercase placeholder:text-slate-400 focus:border-primary focus:ring-0 transition-colors"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-3">
                <Label htmlFor="password" className="text-sm font-black uppercase tracking-widest text-slate-900">
                  Password
                </Label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-12 bg-slate-900 flex items-center justify-center border-r-4 border-slate-900">
                    <Lock className="text-white" size={18} />
                  </div>
                  <Input
                    id="password"
                    data-testid="login-password"
                    type="password"
                    placeholder="ENTER PASSWORD"
                    className="pl-16 h-14 border-4 border-slate-900 rounded-none font-mono text-sm uppercase placeholder:text-slate-400 focus:border-primary focus:ring-0 transition-colors"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center space-x-3 pt-2">
                <Checkbox
                  id="remember"
                  checked={formData.rememberMe}
                  onCheckedChange={(checked) => setFormData({ ...formData, rememberMe: checked })}
                  className="border-2 border-slate-900 rounded-none data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900"
                />
                <label
                  htmlFor="remember"
                  className="text-sm font-bold uppercase tracking-wide text-slate-700 cursor-pointer"
                >
                  Remember Session
                </label>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                data-testid="login-submit-button"
                className="w-full h-16 font-black uppercase tracking-wide text-lg rounded-none border-4 border-slate-900 shadow-[5px_5px_0px_0px_rgba(15,23,42,1)] hover:shadow-[7px_7px_0px_0px_rgba(15,23,42,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all group relative overflow-hidden"
                disabled={loading}
              >
                <span className="relative z-10 flex items-center justify-center">
                  {loading ? 'AUTHENTICATING...' : 'INITIALIZE LOGIN'}
                  {!loading && <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" size={20} />}
                </span>
                {!loading && (
                  <div className="absolute inset-0 bg-accent transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
                )}
              </Button>
            </form>

            {/* Footer Links */}
            <div className="mt-8 pt-6 border-t-4 border-slate-900 space-y-4">
              <div className="text-center">
                <p className="text-sm font-bold uppercase tracking-wide text-slate-600">
                  No Account?{' '}
                  <Link 
                    to="/register" 
                    className="text-primary font-black hover:underline inline-flex items-center gap-1 group"
                  >
                    Register System
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </p>
              </div>

              {/* Demo Credentials */}
              
            </div>
          </div>
        </div>

        {/* Back to Home Link */}
        <div className="mt-6 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900 transition-colors group"
          >
            <ArrowRight size={16} className="rotate-180 group-hover:-translate-x-1 transition-transform" />
            Return to Main System
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}